import React, { useState, useRef, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { BarChart3, RefreshCw, ChevronDown, LogOut, Lightbulb, LayoutDashboard, User } from 'lucide-react';
import type { User as UserType } from '../../types/auth';

interface HeaderProps {
  user: UserType | null;
  onLogout: () => void;
  onRefresh?: () => void;
  title?: string;
  className?: string;
}

export const Header: React.FC<HeaderProps> = ({
  user,
  onLogout,
  onRefresh,
  title = 'Nitrextrader',
  className = '',
}) => {
  const [isProfileMenuOpen, setIsProfileMenuOpen] = useState(false);
  const profileMenuRef = useRef<HTMLDivElement>(null);
  const location = useLocation();

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (profileMenuRef.current && !profileMenuRef.current.contains(e.target as Node)) {
        setIsProfileMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const getInitials = (email: string) => email.charAt(0).toUpperCase();

  const navLinks = [
    { to: '/dashboard',       label: 'Dashboard',  Icon: LayoutDashboard },
    { to: '/ai-analysis-demo', label: 'AI Analysis', Icon: Lightbulb },
  ];

  return (
    <header
      className={`app-header ${className}`}
      style={{ background: 'rgba(10,14,26,0.92)', backdropFilter: 'blur(16px)' }}
    >
      <div className="app-header-inner">
        {/* ── Left: brand + nav ── */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '1.5rem' }}>
          {/* Brand */}
          <Link
            to="/dashboard"
            style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', textDecoration: 'none', flexShrink: 0 }}
          >
            <div style={{
              width: 32, height: 32, borderRadius: 8,
              background: 'linear-gradient(135deg, #3b82f6, #8b5cf6)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              boxShadow: '0 0 12px rgba(59,130,246,0.3)',
              flexShrink: 0,
            }}>
              <BarChart3 size={16} color="#fff" strokeWidth={2.5} />
            </div>
            <span style={{
              fontSize: '1rem', fontWeight: 800, letterSpacing: '-0.03em',
              color: '#f1f5f9', display: 'none',
            }}
              className="sm:block"
            >
              Nitrex<span style={{ color: '#60a5fa' }}>trader</span>
            </span>
          </Link>

          {/* Nav links */}
          <nav className="hidden md:flex" style={{ display: 'none', alignItems: 'center', gap: '0.25rem' }} aria-label="Main navigation">
            {navLinks.map(({ to, label, Icon }) => {
              const active = location.pathname === to;
              return (
                <Link
                  key={to}
                  to={to}
                  style={{
                    display: 'inline-flex', alignItems: 'center', gap: '0.375rem',
                    padding: '0.4375rem 0.75rem',
                    borderRadius: 8,
                    fontSize: '0.8125rem', fontWeight: 600,
                    textDecoration: 'none',
                    transition: 'background 0.18s, color 0.18s',
                    background: active ? 'rgba(59,130,246,0.15)' : 'transparent',
                    color: active ? '#60a5fa' : '#64748b',
                    border: active ? '1px solid rgba(59,130,246,0.25)' : '1px solid transparent',
                    letterSpacing: '-0.01em',
                  }}
                  onMouseEnter={e => {
                    if (!active) {
                      e.currentTarget.style.background = 'rgba(255,255,255,0.05)';
                      e.currentTarget.style.color = '#cbd5e1';
                    }
                  }}
                  onMouseLeave={e => {
                    if (!active) {
                      e.currentTarget.style.background = 'transparent';
                      e.currentTarget.style.color = '#64748b';
                    }
                  }}
                >
                  <Icon size={14} strokeWidth={active ? 2.5 : 2} />
                  {label}
                </Link>
              );
            })}
          </nav>
        </div>

        {/* ── Right: actions ── */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.375rem' }}>
          {onRefresh && (
            <button
              onClick={onRefresh}
              className="btn-icon"
              aria-label="Refresh market data"
              title="Refresh"
            >
              <RefreshCw size={15} strokeWidth={2} />
            </button>
          )}

          {user && (
            <div ref={profileMenuRef} style={{ position: 'relative' }}>
              <button
                onClick={() => setIsProfileMenuOpen(!isProfileMenuOpen)}
                aria-label="User menu"
                aria-expanded={isProfileMenuOpen}
                style={{
                  display: 'flex', alignItems: 'center', gap: '0.5rem',
                  padding: '0.375rem 0.625rem',
                  borderRadius: 8, border: '1px solid transparent',
                  background: 'transparent', cursor: 'pointer',
                  transition: 'background 0.18s, border-color 0.18s',
                  color: '#94a3b8',
                }}
                onMouseEnter={e => {
                  e.currentTarget.style.background = 'rgba(255,255,255,0.05)';
                  e.currentTarget.style.borderColor = 'rgba(255,255,255,0.08)';
                }}
                onMouseLeave={e => {
                  e.currentTarget.style.background = 'transparent';
                  e.currentTarget.style.borderColor = 'transparent';
                }}
              >
                {/* Avatar */}
                <div style={{
                  width: 30, height: 30, borderRadius: '50%',
                  background: 'linear-gradient(135deg, #3b82f6, #8b5cf6)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: '0.75rem', fontWeight: 700, color: '#fff',
                  flexShrink: 0,
                }}>
                  {getInitials(user.email)}
                </div>
                {/* Email — desktop only */}
                <div className="hidden md:block" style={{ display: 'none', textAlign: 'left' }}>
                  <div style={{ fontSize: '0.8rem', fontWeight: 600, color: '#cbd5e1', maxWidth: 140, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {user.email.split('@')[0]}
                  </div>
                  <div style={{ fontSize: '0.7rem', color: '#475569' }}>
                    Member since {new Date(user.created_at).getFullYear()}
                  </div>
                </div>
                <ChevronDown
                  size={13}
                  strokeWidth={2.5}
                  style={{
                    color: '#475569',
                    transition: 'transform 0.2s',
                    transform: isProfileMenuOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                  }}
                />
              </button>

              {/* Dropdown */}
              {isProfileMenuOpen && (
                <div
                  style={{
                    position: 'absolute', right: 0, top: 'calc(100% + 8px)',
                    width: 220,
                    background: '#111827',
                    border: '1px solid #1e2d45',
                    borderRadius: 12,
                    boxShadow: '0 16px 48px rgba(0,0,0,0.5)',
                    overflow: 'hidden',
                    zIndex: 60,
                    animation: 'slideUp 0.18s ease-out forwards',
                  }}
                  role="menu"
                >
                  {/* User info */}
                  <div style={{ padding: '0.875rem 1rem', borderBottom: '1px solid #1e2d45', background: '#1a2235' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.625rem' }}>
                      <div style={{
                        width: 36, height: 36, borderRadius: '50%',
                        background: 'linear-gradient(135deg, #3b82f6, #8b5cf6)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontSize: '0.8125rem', fontWeight: 700, color: '#fff', flexShrink: 0,
                      }}>
                        {getInitials(user.email)}
                      </div>
                      <div>
                        <div style={{ fontSize: '0.8125rem', fontWeight: 600, color: '#e2e8f0', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 140 }}>
                          {user.email}
                        </div>
                        <div style={{ fontSize: '0.7rem', color: '#475569', marginTop: 1 }}>
                          Joined {new Date(user.created_at).toLocaleDateString()}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Profile link */}
                  <div style={{ padding: '0.25rem' }}>
                    <button
                      role="menuitem"
                      style={{
                        display: 'flex', alignItems: 'center', gap: '0.625rem',
                        width: '100%', padding: '0.625rem 0.75rem',
                        borderRadius: 8, border: 'none', background: 'transparent',
                        cursor: 'pointer', fontSize: '0.8125rem', fontWeight: 500,
                        color: '#94a3b8', transition: 'background 0.15s, color 0.15s',
                        textAlign: 'left',
                      }}
                      onMouseEnter={e => {
                        e.currentTarget.style.background = '#1a2235';
                        e.currentTarget.style.color = '#e2e8f0';
                      }}
                      onMouseLeave={e => {
                        e.currentTarget.style.background = 'transparent';
                        e.currentTarget.style.color = '#94a3b8';
                      }}
                    >
                      <User size={14} strokeWidth={2} />
                      Profile
                    </button>

                    <div style={{ margin: '0.25rem 0', height: 1, background: '#1e2d45' }} />

                    <button
                      role="menuitem"
                      onClick={() => { setIsProfileMenuOpen(false); onLogout(); }}
                      style={{
                        display: 'flex', alignItems: 'center', gap: '0.625rem',
                        width: '100%', padding: '0.625rem 0.75rem',
                        borderRadius: 8, border: 'none', background: 'transparent',
                        cursor: 'pointer', fontSize: '0.8125rem', fontWeight: 500,
                        color: '#94a3b8', transition: 'background 0.15s, color 0.15s',
                        textAlign: 'left',
                      }}
                      onMouseEnter={e => {
                        e.currentTarget.style.background = 'rgba(239,68,68,0.1)';
                        e.currentTarget.style.color = '#f87171';
                      }}
                      onMouseLeave={e => {
                        e.currentTarget.style.background = 'transparent';
                        e.currentTarget.style.color = '#94a3b8';
                      }}
                    >
                      <LogOut size={14} strokeWidth={2} />
                      Sign out
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Mobile nav strip */}
      <div
        className="md:hidden"
        style={{
          borderTop: '1px solid #1e2d45',
          padding: '0.375rem 1rem',
          display: 'flex', gap: '0.25rem',
          overflowX: 'auto',
        }}
      >
        {navLinks.map(({ to, label, Icon }) => {
          const active = location.pathname === to;
          return (
            <Link
              key={to}
              to={to}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: '0.375rem',
                padding: '0.4375rem 0.75rem',
                borderRadius: 8, flexShrink: 0,
                fontSize: '0.8125rem', fontWeight: 600,
                textDecoration: 'none',
                background: active ? 'rgba(59,130,246,0.15)' : 'transparent',
                color: active ? '#60a5fa' : '#64748b',
                border: active ? '1px solid rgba(59,130,246,0.25)' : '1px solid transparent',
                whiteSpace: 'nowrap',
              }}
            >
              <Icon size={13} strokeWidth={active ? 2.5 : 2} />
              {label}
            </Link>
          );
        })}
      </div>
    </header>
  );
};
